import React from 'react';
import { TopUser } from '../../modules/public/rank/types';
import { Table } from 'antd';
import { Avatar } from 'antd';
import { TrophyIcon } from '../../assets/images/TrophyIcon';
import { formatNumber } from '../../helpers/formatNumber';
import { shortenEmail } from '../../helpers';
import { PNL_CONSTANTS, calculatePnLPercentage } from '../../constants/pnlConstants';

interface UserRankProps {
    data: Array<TopUser>;
    highlightTop?: boolean;
}

const UserRank: React.FC<UserRankProps> = ({ data, highlightTop = false }) => {
    const top3Users = data.slice(0, 3);
    const otherUsers = data.slice(3);
    
    // Use constant from pnlConstants
    const calculatePnL = calculatePnLPercentage;

    const columns = [
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            width: '5%',
            render: (rank: number) => <div className="rank-number">{rank}</div>
        },
        {
            title: 'User name',
            dataIndex: 'username',
            key: 'username',
            width: '20%',
            render: (name: string) => (
                <div className="user-info">
                    <Avatar src="https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg" />
                    <div className="user-details">
                        <div className="name">{name}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: '20%',
            render: (email: string) => (
                <span className='text-white' >
                    {shortenEmail(email)}
                </span>
            )
        },
        // {
        //     title: 'ROIC',
        //     dataIndex: 'ROIC',
        //     key: 'ROIC',
        //     width: '15%',
        //     render: (ROIC: number) => {
        //         const hasIncreasedROIC = ROIC >= 0;
        //         return (
        //             <span className={`stat-value ${hasIncreasedROIC ? 'positive' : 'negative'}`}>
        //                 {hasIncreasedROIC ? '+' : '-'}{Math.abs(ROIC).toFixed(2)}%
        //                 {hasIncreasedROIC ? '↑' : '↓'}
        //             </span>
        //         );
        //     }
        // },
        {
            title: 'PnL (VND)',
            dataIndex: 'increasedAmount',
            key: 'increasedAmount',
            width: '25%',
            render: (amount: number) => {
                const isZero = amount === 0;
                const hasIncreased = amount > 0;
                const colorClass = isZero ? 'neutral' : (hasIncreased ? 'positive' : 'negative');
                const arrow = isZero ? '' : (hasIncreased ? '↑' : '↓');
                const sign = isZero ? '' : (hasIncreased ? '+' : '-');
                
                return (
                    <span className={`stat-value ${colorClass}`}>
                        {sign}{Math.abs(amount).toLocaleString()} VND
                        {arrow}
                    </span>
                );
            }
        },
        {
            title: 'PnL (%)',
            dataIndex: 'increasedAmount',
            key: 'increasedAmountPercent',
            width: '15%',
            render: (amount: number, item) => {
                const pnlPercentage = calculatePnL(amount, item?.debug?.totalInvested);
                const isZero = amount === 0;
                const hasIncreased = amount > 0;
                const colorClass = isZero ? 'neutral' : (hasIncreased ? 'positive' : 'negative');
                const arrow = isZero ? '' : (hasIncreased ? '↑' : '↓');
                const sign = isZero ? '' : (hasIncreased ? '+' : '');
                
                return (
                    <span className={`stat-value ${colorClass}`}>
                        {sign}{pnlPercentage.toFixed(2)}%
                        {arrow}
                    </span>
                );
            }
        }
    ];

    return (
        <div className="leaderboard-container">
            <div className="top-users-section">
                {top3Users.map((user, index) => {
                    console.log(user);
                    const position = index === 0 ? 'center' : index === 1 ? 'left' : 'right';
                    const isZero = user.increasedAmount === 0;
                    const hasIncreased = user.increasedAmount > 0;
                    const hasIncreasedROIC = user.ROIC >= 0;

                    return (
                        <div key={user.uid} className={`top-user-card ${position} ${(highlightTop && index === 0) ? 'pulse-glow' : ''}`}>
                            <div className="rank-badge">
                                <TrophyIcon isFirst={index === 0} className="trophy-icon" />
                                <span className="rank-number">{user.rank}</span>
                            </div>
                            <div className="user-header">
                                <div className="user-info">
                                    <Avatar
                                        size={64}
                                        src="https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg"
                                    />
                                    <div className="user-details">
                                        <p className="name">{user?.username || ''}</p>
                                    </div>
                                </div>
                                {/* <Button className="follow-btn">+ Follow</Button> */}
                            </div>

                            <div className="stats-container">
                                <div className="stat-row">
                                    <span className="stat-label">PnL (VND)</span>
                                    <span className={`stat-value ${hasIncreased ? 'positive' : 'negative'}`}>
                                        {hasIncreased ? '+' : '-'}{Math.abs(user.increasedAmount).toLocaleString()} VND
                                        {hasIncreased ? '↑' : '↓'}
                                    </span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">PnL (%)</span>
                                    <span className={`stat-value ${hasIncreased ? 'positive' : 'negative'}`}>
                                        {hasIncreased ? '+' : ''}{calculatePnL(user.increasedAmount, user?.debug?.totalInvested).toFixed(2)}%
                                        {hasIncreased ? '↑' : '↓'}
                                    </span>
                                </div>
                                <div className="stat-row">
                                    <span className="stat-label">Email</span>
                                    <span className='text-white' >
                                        {shortenEmail((user as any)?.email)}
                                    </span>
                                </div>
                            </div>

                        </div>
                    );
                })}
            </div>

            {otherUsers.length > 0 && (
                <Table
                    dataSource={otherUsers}
                    columns={columns}
                    pagination={false}
                    className="users-table"
                />
            )}
        </div>
    );
};

export default UserRank;
